/** BoldSign e-signature — v1 expenses-calendar-materials.js esignOpen/esignUpload,
 * same contract:
 *   GET  /api/boldsign/config     → { configured: boolean }   (gate — needs BOLDSIGN_API_KEY)
 *   POST /api/boldsign/embed-new  (multipart) → { sendUrl }    (rendered in an iframe)
 *
 * The server owns the BoldSign API key and resolves the org from the Bearer
 * token (see lib/backend.ts). Without the key, config.configured is false and
 * the UI shows the "add the key" hint instead of the builder.
 *
 * embed-new is multipart (a PDF + a signers JSON blob), so — like drive/api.ts
 * uploadDriveFile — it goes through a raw fetch rather than backendFetch (which
 * JSON-encodes the body and would clobber the multipart boundary). Auth is sent
 * BOTH as a Bearer header (v1's boldsign path, apiFetch) and as a `token` form
 * field (the proven drive-upload path), so it works whichever the server reads.
 * Never set Content-Type by hand — the browser must set the multipart boundary. */
import { API_BASE } from "../../config";
import { BackendError, backendFetch } from "../../lib/backend";

export interface EsignRecipient {
  name: string;
  email: string;
}

/** v1's gate. Any failure (network, 401, missing key) resolves to false so the
 * modal shows the single "add BOLDSIGN_API_KEY" hint and never crashes. */
export async function fetchEsignConfig(
  token: string | null,
  opts?: { signal?: AbortSignal; onWake?: () => void },
): Promise<boolean> {
  try {
    const j = await backendFetch<{ configured?: unknown }>(token, "/api/boldsign/config", {
      timeoutMs: 20000,
      ...(opts?.signal ? { signal: opts.signal } : {}),
      ...(opts?.onWake ? { onWake: opts.onWake } : {}),
    });
    return j?.configured === true;
  } catch {
    return false;
  }
}

// ---- templates (Settings → Templates, parity item #1 2026-07-23) -----------
// v1's BoldSign template manager: list / create-from-PDF (embedded builder) /
// delete / send-from-template. Same backend routes v1 hits (routes/boldsign.py).

export interface EsignTemplate {
  templateId: string;
  title: string;
}

/** Pure narrow for GET /api/boldsign/templates — exported for tests. Rows
 * without a templateId are dropped (they can't be sent or deleted). */
export function narrowTemplates(raw: unknown): EsignTemplate[] {
  const o = raw !== null && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const list = Array.isArray(o["templates"]) ? o["templates"] : [];
  const out: EsignTemplate[] = [];
  for (const t of list) {
    if (t === null || typeof t !== "object") continue;
    const r = t as Record<string, unknown>;
    const id = typeof r["templateId"] === "string" && r["templateId"] ? r["templateId"] : "";
    if (!id) continue;
    out.push({ templateId: id, title: typeof r["title"] === "string" && r["title"] ? r["title"] : "Template" });
  }
  return out;
}

export async function fetchEsignTemplates(token: string | null, signal?: AbortSignal): Promise<EsignTemplate[]> {
  const j = await backendFetch<unknown>(token, "/api/boldsign/templates", {
    timeoutMs: 30000,
    ...(signal ? { signal } : {}),
  });
  return narrowTemplates(j);
}

/** POST /api/boldsign/delete-template — templateId rides as a QUERY param
 * (FastAPI simple-arg convention on that route, matching v1's call). */
export async function deleteEsignTemplate(token: string | null, templateId: string): Promise<void> {
  await backendFetch(token, "/api/boldsign/delete-template?templateId=" + encodeURIComponent(templateId), {
    method: "POST",
    timeoutMs: 30000,
  });
}

/** POST /api/boldsign/embed-create-template (multipart PDF) → the embedded
 * BUILDER url. The caller opens it in a new tab; the user drops fields and
 * hits Save there, then refreshes the list here. Multipart = raw fetch, same
 * discipline as esignEmbedNew below. */
export async function createEsignTemplate(token: string | null, file: File, title: string): Promise<string> {
  const fd = new FormData();
  fd.append("file", file, file.name);
  fd.append("title", title || "Template");
  if (token) fd.append("token", token);
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), 120000);
  try {
    const res = await fetch(API_BASE + "/api/boldsign/embed-create-template", {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
      signal: ctl.signal,
    });
    const text = await res.text();
    let data: unknown = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = text; }
    if (!res.ok) {
      const detail = data !== null && typeof data === "object" && typeof (data as { detail?: unknown }).detail === "string"
        ? (data as { detail: string }).detail
        : String(res.status);
      throw new BackendError(res.status, detail.slice(0, 160));
    }
    const obj = data !== null && typeof data === "object" ? (data as Record<string, unknown>) : {};
    const url = ["createUrl", "editUrl", "embedUrl", "url"]
      .map((k) => (typeof obj[k] === "string" ? (obj[k] as string) : ""))
      .find((u) => !!u) ?? "";
    if (!url) throw new BackendError(res.status, "The server did not return a builder URL");
    return url;
  } finally {
    clearTimeout(t);
  }
}

export interface TemplateSendInput {
  templateId: string;
  title: string;
  signers: EsignRecipient[];
  /** optional lead/deal to log + file the signed PDF against */
  leadKey?: string;
}

/** POST /api/boldsign/send-template → documentId. The server logs the doc and
 * (via its webhook/sweep) files the signed PDF to Drive under the property. */
export async function sendEsignTemplate(token: string | null, input: TemplateSendInput): Promise<string> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/boldsign/send-template", {
    method: "POST",
    timeoutMs: 60000,
    body: {
      templateId: input.templateId,
      title: input.title,
      signers: input.signers.map((s) => ({ name: s.name, emailAddress: s.email })),
      ...(input.leadKey ? { lead_key: input.leadKey, property_label: input.leadKey } : {}),
    },
  });
  return typeof j?.["documentId"] === "string" ? (j["documentId"] as string) : "";
}

export interface EsignEmbedInput {
  file: File;
  title: string;
  /** the property/lead label — v1 sends it as both lead_key and property_label */
  leadKey: string;
  recipients: EsignRecipient[];
}

/** POST /api/boldsign/embed-new (multipart) → the BoldSign `sendUrl` to embed.
 * Mirrors v1 esignUpload's field set (file, title, org_id, lead_key,
 * property_label, signers JSON). Single attempt — no blind retry that could
 * double-create a document; throws BackendError on a non-2xx or a missing URL
 * so the caller shows an inline message. */
export async function esignEmbedNew(
  token: string | null,
  input: EsignEmbedInput,
  signal?: AbortSignal,
): Promise<string> {
  const fd = new FormData();
  fd.append("file", input.file, input.file.name);
  fd.append("title", input.title);
  fd.append("org_id", ""); // v1 sends the org id here; the server also resolves it from the token
  fd.append("lead_key", input.leadKey);
  fd.append("property_label", input.leadKey);
  fd.append("signers", JSON.stringify(input.recipients));
  if (token) fd.append("token", token);

  const ctl = new AbortController();
  const onAbort = () => ctl.abort();
  signal?.addEventListener("abort", onAbort);
  const t = setTimeout(() => ctl.abort(), 120000); // large PDFs
  try {
    const res = await fetch(API_BASE + "/api/boldsign/embed-new", {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
      signal: ctl.signal,
    });
    const text = await res.text();
    let data: unknown = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = text;
    }
    if (!res.ok) {
      const detail =
        data !== null && typeof data === "object" && typeof (data as { detail?: unknown }).detail === "string"
          ? (data as { detail: string }).detail
          : String(res.status);
      throw new BackendError(res.status, detail.slice(0, 160));
    }
    const obj = data !== null && typeof data === "object" ? (data as Record<string, unknown>) : {};
    const url =
      typeof obj["sendUrl"] === "string"
        ? (obj["sendUrl"] as string)
        : typeof obj["embedUrl"] === "string"
          ? (obj["embedUrl"] as string)
          : "";
    if (!url) throw new BackendError(res.status, "The server did not return a signing URL");
    return url;
  } finally {
    clearTimeout(t);
    signal?.removeEventListener("abort", onAbort);
  }
}
